/**
 * Emergency PDF Export Utility
 *
 * Generates a printable Emergency Card PDF with P1 data (client-side, no
 * server round-trip): demographics, allergies, medications, emergency contacts
 * and the QR code.
 *
 * Labels come from i18n and dates, height and weight follow the app language
 * (US units and MM/DD/YYYY in English). Stored values stay metric/ISO.
 */

import {
  systemsOf,
  useUnitPreferencesStore,
} from "../stores/unitPreferencesStore";
import jsPDF from "jspdf";
import { translations } from "../locales/translations";
import { useI18nStore } from "../stores/i18nStore";
import type { Language, Translation } from "../types/i18n";
import { formatDate as formatLocalDate } from "./dateLocalization";
import { formatQuantity } from "./unitLocalization";

interface PDFPatient {
  name: string;
  dateOfBirth?: string | null;
  age?: number | null;
  gender?: string;
  bloodType?: string | null;
  height?: number | null;
  weight?: number | null;
  nationality?: string | null;
}

interface PDFAllergyItem {
  allergen: string;
  criticality: string;
  reactionType?: string;
}

interface PDFMedicationItem {
  medicationName: string;
  dosage: string;
  frequency?: string;
  route?: string;
}

interface PDFContact {
  name: string;
  relationship: string;
  phone: string;
  email?: string;
}

interface EmergencyCardData {
  patient: PDFPatient;
  allergies: PDFAllergyItem[];
  medications: PDFMedicationItem[];
  emergencyContacts: PDFContact[];
  qrCodeDataUrl?: string;
  generatedAt?: string;
}

const LINE_HEIGHT = 6;
const MARGIN = 14;
const PAGE_WIDTH = 210;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

function addSectionTitle(doc: jsPDF, text: string, y: number): number {
  doc.setFillColor(220, 38, 38);
  doc.rect(MARGIN, y, CONTENT_WIDTH, 7, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(text.toUpperCase(), MARGIN + 2, y + 5);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  return y + 10;
}

function checkNewPage(doc: jsPDF, y: number, needed = 10): number {
  if (y + needed > 280) {
    doc.addPage();
    return 20;
  }
  return y;
}

function formatDate(
  isoOrString: string | null | undefined,
  language: Language,
): string {
  if (!isoOrString) return "–";
  return formatLocalDate(isoOrString, language, "medium") || "–";
}

/** Strings for `language`, English filling any key it lacks. */
function stringsFor(language: Language): Translation {
  if (language === useI18nStore.getState().language) {
    return useI18nStore.getState().t;
  }
  return { ...translations.en, ...(translations[language] ?? {}) };
}

function genderLabel(gender: string | undefined, t: Translation): string {
  if (!gender) return "–";
  if (gender === "male") return t.male;
  if (gender === "female") return t.female;
  if (gender === "other") return t.other;
  if (gender === "unknown") return t.unknown;
  return gender;
}

/** Criticality codes are FHIR values; only the printed word is translated. */
function criticalityLabel(code: string, t: Translation): string {
  if (code === "high") return t.emergxPdfCritical;
  if (code === "low") return t.emergxPdfMild;
  return t.emergxPdfNotAssessed;
}

/**
 * Generates the Emergency Card PDF and triggers browser download.
 *
 * `language` defaults to the app language, which is what the PDF should
 * follow; pass one only to print a card in a different language on purpose.
 */
export function downloadEmergencyCardPDF(
  data: EmergencyCardData,
  language: Language = useI18nStore.getState().language,
): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const t = stringsFor(language);
  // The user's explicit unit choice, as on screen; none: the language decides.
  const units = useUnitPreferencesStore.getState();
  const systems =
    units.useNationalityDefaults || !units.preferences
      ? undefined
      : systemsOf(units.preferences);

  let y = MARGIN;

  // ── Header ────────────────────────────────────────────────────────────────
  doc.setFillColor(220, 38, 38);
  doc.rect(0, 0, PAGE_WIDTH, 20, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(`⚕ ${t.emergxPdfTitle}`, MARGIN, 13);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("HealthPass · healthpass.app", PAGE_WIDTH - MARGIN, 13, {
    align: "right",
  });
  doc.setTextColor(0, 0, 0);

  y = 28;

  // ── Patient Demographics ──────────────────────────────────────────────────
  y = addSectionTitle(doc, t.emergxPdfPatientInfo, y);
  doc.setFontSize(10);
  const { patient } = data;
  const rows: [string, string][] = [
    [t.name, patient.name || "–"],
    [t.emergxPdfDateOfBirth, formatDate(patient.dateOfBirth, language)],
    [t.emergxPdfAge, patient.age ? `${patient.age}` : "–"],
    [t.emergxPdfGender, genderLabel(patient.gender, t)],
    [t.emergxPdfBloodType, patient.bloodType || "–"],
    [
      t.emergxPdfHeightWeight,
      // Stored as cm / kg; printed as ft-in / lb in English.
      `${patient.height ? formatQuantity("height", patient.height, language, systems) : "–"} / ${patient.weight ? formatQuantity("weight", patient.weight, language, systems) : "–"}`,
    ],
    [t.emergxPdfNationality, patient.nationality || "–"],
  ];
  for (const [label, value] of rows) {
    y = checkNewPage(doc, y);
    doc.setFont("helvetica", "bold");
    doc.text(label + ":", MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.text(value, MARGIN + 45, y);
    y += LINE_HEIGHT;
  }

  y += 4;

  // ── Allergies ─────────────────────────────────────────────────────────────
  y = checkNewPage(doc, y, 20);
  y = addSectionTitle(doc, t.emergxPdfAllergies, y);
  if (data.allergies.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text(t.emergxPdfNoAllergies, MARGIN, y);
    y += LINE_HEIGHT;
  } else {
    for (const a of data.allergies) {
      y = checkNewPage(doc, y);
      const critColor: [number, number, number] =
        a.criticality === "high" ? [220, 38, 38] : [234, 88, 12];
      doc.setTextColor(...critColor);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(`▶ ${a.allergen}`, MARGIN, y);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      const detail = [criticalityLabel(a.criticality, t), a.reactionType]
        .filter(Boolean)
        .join(" | ");
      doc.text(detail, MARGIN + 55, y);
      y += LINE_HEIGHT;
    }
  }

  y += 4;

  // ── Medications ───────────────────────────────────────────────────────────
  y = checkNewPage(doc, y, 20);
  y = addSectionTitle(doc, t.emergxPdfMedications, y);
  if (data.medications.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text(t.emergxPdfNoMedications, MARGIN, y);
    y += LINE_HEIGHT;
  } else {
    for (const m of data.medications) {
      y = checkNewPage(doc, y);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(`• ${m.medicationName}`, MARGIN, y);
      doc.setFont("helvetica", "normal");
      const detail = [m.dosage, m.frequency, m.route]
        .filter(Boolean)
        .join(" | ");
      doc.text(detail, MARGIN + 55, y);
      y += LINE_HEIGHT;
    }
  }

  y += 4;

  // ── Emergency Contacts ────────────────────────────────────────────────────
  y = checkNewPage(doc, y, 20);
  y = addSectionTitle(doc, t.emergxPdfContacts, y);
  if (data.emergencyContacts.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text(t.emergxPdfNoContacts, MARGIN, y);
    y += LINE_HEIGHT;
  } else {
    for (const c of data.emergencyContacts) {
      y = checkNewPage(doc, y);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(`${c.name} (${c.relationship})`, MARGIN, y);
      doc.setFont("helvetica", "normal");
      doc.text(c.phone, MARGIN + 70, y);
      y += LINE_HEIGHT;
      if (c.email) {
        doc.text(c.email, MARGIN + 4, y);
        y += LINE_HEIGHT;
      }
    }
  }

  // ── QR Code ───────────────────────────────────────────────────────────────
  if (data.qrCodeDataUrl) {
    y = checkNewPage(doc, y, 60);
    y += 4;
    y = addSectionTitle(doc, t.emergxPdfQrTitle, y);
    try {
      doc.addImage(data.qrCodeDataUrl, "PNG", MARGIN, y, 50, 50);
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.text(t.emergxPdfQrHint, MARGIN + 54, y + 10);
    } catch {
      // QR image failed – skip silently
    }
    y += 55;
  }

  // ── Footer ────────────────────────────────────────────────────────────────
  const generatedAt = formatDate(
    data.generatedAt ?? new Date().toISOString(),
    language,
  );
  doc.setFontSize(7);
  doc.setTextColor(150);
  doc.text(
    `${t.emergxPdfGeneratedOn.replace("{date}", generatedAt)} · HealthPass · ${t.emergxPdfDisclaimer}`,
    MARGIN,
    290,
  );

  doc.save(
    `HealthPass_Emergency_Card_${data.patient.name?.replace(/\s+/g, "_") || "patient"}.pdf`,
  );
}
